/**
 * AppellationFactsCard — card chiffrée affichée à côté de la carte iframe
 * dans les étapes "Se situer" et "Territoire" du voyage Academy.
 *
 * Les données sont extraites par DeepSeek + Tavily et cachées côté
 * backend (`/api/academy/appellations/{slug}/facts`).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Mountain, MapPin, CloudRain, Thermometer, Pickaxe, Grape, Building2,
  Calendar, Wine, Trophy, Loader2, ExternalLink,
} from "lucide-react";

const BADGE_DEFS = [
  { key: "surface_ha",        Icon: MapPin,     label: "Surface",             unit: "ha",   fmt: (v) => v.toLocaleString("fr-FR") },
  { key: "altitude_min_m",    Icon: Mountain,   label: "Altitude",            unit: "m",    pairWith: "altitude_max_m",
    fmt: (v, f) => f.altitude_max_m ? `${v}–${f.altitude_max_m}` : `${v}` },
  { key: "climate_type",      Icon: CloudRain,  label: "Climat",              unit: "",     fmt: (v) => v },
  { key: "avg_temp_c",        Icon: Thermometer,label: "Temp. moyenne",       unit: "°C",   fmt: (v) => v },
  { key: "avg_rainfall_mm",   Icon: CloudRain,  label: "Pluviométrie",        unit: "mm/an",fmt: (v) => v.toLocaleString("fr-FR") },
  { key: "soil_dominant",     Icon: Pickaxe,    label: "Sols",                unit: "",     fmt: (v) => v },
  { key: "grape_count",       Icon: Grape,      label: "Cépages",             unit: "",     pairWith: "grapes",
    fmt: (v, f) => f.grapes?.length ? f.grapes.join(", ") : `${v}` },
  { key: "producers_count",   Icon: Building2,  label: "Producteurs",         unit: "",     fmt: (v) => v },
  { key: "aoc_year",          Icon: Calendar,   label: "AOC depuis",          unit: "",     fmt: (v) => v },
  { key: "production_hl",     Icon: Wine,       label: "Production",          unit: "hL/an",fmt: (v) => v.toLocaleString("fr-FR") },
  { key: "hierarchy",         Icon: Trophy,     label: "Hiérarchie",          unit: "",     fmt: (v) => v.join(" · ") },
];

export default function AppellationFactsCard({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/academy/appellations/${encodeURIComponent(slug)}/facts`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#1F3A5F]/10 bg-white p-4 text-sm text-neutral-500 flex items-center gap-2"
           data-testid="facts-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        geoVINUM IA réunit les chiffres clés de cette appellation…
      </div>
    );
  }

  if (!data?.facts) return null;
  const f = data.facts;
  const badges = BADGE_DEFS
    .map((b) => ({ ...b, raw: f[b.key] }))
    .filter((b) => b.raw !== null && b.raw !== undefined && b.raw !== "");

  if (badges.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[#1F3A5F]/10 bg-white p-4 shadow-sm" data-testid="facts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold">
          Fiche d'identité
        </div>
        {f.sources?.length > 0 && (
          <details className="text-[10px] text-neutral-400">
            <summary className="cursor-pointer hover:text-[#E5276A]">
              {f.sources.length} source{f.sources.length > 1 ? "s" : ""}
            </summary>
            <ul className="mt-1 space-y-0.5 text-right">
              {f.sources.map((s) => (
                <li key={s}>
                  <a href={s} target="_blank" rel="noreferrer"
                     className="hover:text-[#E5276A] inline-flex items-center gap-0.5">
                    {new URL(s).hostname.replace("www.", "")}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {badges.map((b) => {
          const Ic = b.Icon;
          const val = b.fmt(b.raw, f);
          return (
            <div key={b.key} className="rounded-xl bg-gradient-to-br from-[#FBF7F3] to-white p-2.5 border border-neutral-100">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                <Ic className="w-3 h-3 text-[#E5276A]" />
                {b.label}
              </div>
              <div className="text-[#1F3A5F] font-semibold text-sm leading-tight">
                {val} <span className="text-[11px] text-neutral-400 font-normal">{b.unit}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
